/**
 * Authentication Utility Functions
 * Handles token storage, retrieval, and validation
 */

const TOKEN_KEY = 'authToken';
const USER_KEY = 'authUser';

/**
 * Save token to localStorage
 * @param {string} token - JWT token
 */
export const saveToken = (token) => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (error) {
    console.error('Error saving token:', error);
  }
};

/**
 * Get token from localStorage
 * @returns {string|null} JWT token or null
 */
export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    console.error('Error getting token:', error);
    return null;
  }
};

/**
 * Save user data to localStorage
 * @param {object} user - User object
 */
export const saveUser = (user) => {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch (error) {
    console.error('Error saving user:', error);
  }
};

/**
 * Get user data from localStorage
 * @returns {object|null} User object or null
 */
export const getUser = () => {
  try {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error('Error getting user:', error);
    return null;
  }
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if token exists and valid
 */
export const isAuthenticated = () => {
  const token = getToken();
  return !!token;
};

/**
 * Check if token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if expired
 */
export const isTokenExpired = (token) => {
  try {
    if (!token) return true;

    // Decode token (JWT format: header.payload.signature)
    const parts = token.split('.');
    if (parts.length !== 3) return true;

    const payload = JSON.parse(atob(parts[1]));
    const currentTime = Math.floor(Date.now() / 1000);

    return payload.exp && payload.exp < currentTime;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

/**
 * Logout user - clear localStorage
 */
export const logout = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch (error) {
    console.error('Error logging out:', error);
  }
};

/**
 * Get authorization header for API requests
 * @returns {object} Header object with Bearer token
 */
export const getAuthHeader = () => {
  const token = getToken();
  return {
    Authorization: `Bearer ${token}`
  };
};

/**
 * Get token expiration time in minutes
 * @param {string} token - JWT token
 * @returns {number|null} Minutes until expiration or null
 */
export const getTokenExpirationTime = (token) => {
  try {
    if (!token) return null;

    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    const timeLeft = payload.exp - currentTime;

    return Math.ceil(timeLeft / 60); // Convert to minutes
  } catch (error) {
    console.error('Error getting token expiration:', error);
    return null;
  }
};

/**
 * Decode JWT token (without verification)
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null
 */
export const decodeToken = (token) => {
  try {
    if (!token) return null;

    const parts = token.split('.');
    if (parts.length !== 3) return null;

    return JSON.parse(atob(parts[1]));
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Check if user is ADMIN
 * @returns {boolean} True if user has ADMIN roleType
 */
export const isAdmin = () => {
  const user = getUser();
  return user?.role?.roleType === 'ADMIN';
};

/**
 * Check if user is EXCO (Executive)
 * @returns {boolean} True if user has EXCO roleType
 */
export const isExco = () => {
  const user = getUser();
  return user?.role?.roleType === 'EXCO';
};

/**
 * Check if user is EDITOR
 * @returns {boolean} True if user has EDITOR roleType
 */
export const isEditor = () => {
  const user = getUser();
  return user?.role?.roleType === 'EDITOR';
};

/**
 * Check if user is IT_HEAD
 * @returns {boolean} True if user has IT_HEAD roleType
 */
export const isItHead = () => {
  const user = getUser();
  return user?.role?.roleType === 'IT_HEAD';
};

/**
 * Check if user has specific role type
 * @param {string} roleType - Role type to check (ADMIN, EXCO, EDITOR, IT_HEAD)
 * @returns {boolean} True if user has the specified roleType
 */
export const hasRoleType = (roleType) => {
  const user = getUser();
  return user?.role?.roleType === roleType;
};

const auth = {
  saveToken,
  getToken,
  saveUser,
  getUser,
  isAuthenticated,
  isTokenExpired,
  logout,
  getAuthHeader,
  getTokenExpirationTime,
  decodeToken,
  isAdmin,
  isExco,
  isEditor,
  isItHead,
  hasRoleType
};

export default auth;
